<template>
  <div class="container px-sm-0 bg-light quiz-wrapper" ref="quizRoot">
    <div class="row adjust-margin">
      <div class="col">
        <div class="container px-sm-5 py-4">

          <!-- Loading -->
          <div v-if="phase === 'loading'" class="text-center py-5">
            <div class="spinner-border text-primary" role="status">
              <span class="visually-hidden">Chargement...</span>
            </div>
          </div>

          <!-- Quiz disabled by supervisor -->
          <div v-else-if="phase === 'disabled'" class="alert alert-secondary text-center" role="alert">
            <h3 class="mb-3">Test indisponible</h3>
            <p class="mb-0">
              Le test n'est pas ouvert pour le moment. Revenez plus tard ou contactez l'organisation si vous avez des questions.
            </p>
          </div>

          <!-- Dossier not submitted -->
          <div v-else-if="phase === 'not_submitted'" class="alert alert-warning text-center" role="alert">
            <h3 class="mb-3">Test indisponible</h3>
            <p class="mb-3">
              Le test n'est accessible qu'aux candidats ayant déjà soumis leur dossier de candidature.
            </p>
            <router-link :to="{ name: 'NewCandidacy' }" class="btn btn-primary">
              Compléter ma candidature
            </router-link>
          </div>

          <!-- Already done -->
          <div v-else-if="phase === 'done'" class="text-center py-5">
            <div class="alert" :class="doneAlertClass" role="alert">
              <h3 class="mb-3">{{ doneTitle }}</h3>
              <p class="mb-0">{{ doneMessage }}</p>
            </div>
          </div>

          <!-- Intro -->
          <div v-else-if="phase === 'intro'">
            <h2 class="section-title text-uppercase mb-4">{{ quizTitle }}</h2>

            <div class="alert alert-info" role="alert">
              <ul class="mb-0 ps-3">
                <li>Ce test comporte <strong>{{ questionsCount }} questions</strong>.</li>
                <li>Durée totale : <strong>{{ formattedDuration }}</strong>.</li>
              </ul>
            </div>

            <div class="alert alert-warning" role="alert">
              <strong>Important :</strong> assurez-vous d'avoir une connexion internet stable.
              <strong>Vous n'aurez qu'une seule tentative.</strong> Dès que vous commencez, le chronomètre démarre et
              <strong>vous ne pouvez plus recommencer</strong>. Quitter l'onglet ou
              le plein écran sera détecté et enregistré.
            </div>

            <div class="text-center mt-4">
              <button class="btn btn-primary btn-lg" @click="startQuiz" :disabled="starting">
                {{ starting ? 'Démarrage...' : 'Commencer le test' }}
              </button>
            </div>
          </div>

          <!-- In progress -->
          <div v-else-if="phase === 'in_progress'">
            <div class="quiz-topbar d-flex justify-content-between align-items-center mb-3">
              <div class="fw-bold">
                {{ answeredCount }} / {{ questions.length }} répondues
              </div>
              <div class="quiz-timer" :class="{ 'text-danger': remainingSeconds <= 60 }">
                Temps restant : {{ formattedRemaining }}
              </div>
            </div>

            <div v-if="cheatViolations > 0" class="alert alert-danger py-2" role="alert">
              {{ cheatViolations }} activité{{ cheatViolations > 1 ? 's' : '' }} suspecte{{ cheatViolations > 1 ? 's' : '' }} détectée{{ cheatViolations > 1 ? 's' : '' }}.
              Restez sur cette page en plein écran jusqu'à la fin du test.
            </div>

            <div
              v-for="question in questions"
              :key="question.id"
              class="card mb-3 quiz-question"
            >
              <div class="card-body">
                <h5 class="card-title">
                  <span class="badge bg-primary me-2">{{ question.number }}</span>
                  {{ question.text }}
                </h5>
                <div class="mt-3">
                  <div
                    v-for="option in question.options"
                    :key="option.index"
                    class="form-check quiz-option"
                  >
                    <input
                      class="form-check-input"
                      type="radio"
                      :name="'question-' + question.id"
                      :id="'q' + question.id + 'o' + option.index"
                      :value="option.index"
                      v-model="answers[question.id]"
                    />
                    <label
                      class="form-check-label"
                      :for="'q' + question.id + 'o' + option.index"
                    >
                      {{ option.text }}
                    </label>
                  </div>
                </div>
              </div>
            </div>

            <div class="text-center my-4">
              <button class="btn btn-success btn-lg" @click="confirmSubmit" :disabled="submitting">
                {{ submitting ? 'Envoi...' : 'Terminer et soumettre' }}
              </button>
            </div>
          </div>

        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from "axios";
import Swal from "sweetalert2";

export default {
  inject: ["ToastError", "ToastWarning", "AlertWarning"],
  data() {
    return {
      phase: "loading",
      quizTitle: "",
      questionsCount: 0,
      durationSeconds: 0,
      remainingSeconds: 0,
      questions: [],
      answers: {},
      starting: false,
      submitting: false,
      timerId: null,
      syncTimerId: null,
      deadlineAt: null,
      timerSyncIntervalMs: 30000,
      cheatViolations: 0,
      lastViolationAt: 0,
      antiCheatBound: false,
      finished: false,
      submissionExpired: false,
      // Silent axios instance (no global loader flashing) for anti-cheat pings.
      silentHttp: null,
    };
  },
  computed: {
    formattedDuration() {
      const minutes = Math.round(this.durationSeconds / 60);
      return minutes + " minutes";
    },
    formattedRemaining() {
      const total = Math.max(0, this.remainingSeconds);
      const minutes = Math.floor(total / 60);
      const seconds = total % 60;
      return (
        String(minutes).padStart(2, "0") + ":" + String(seconds).padStart(2, "0")
      );
    },
    answeredCount() {
      return Object.values(this.answers).filter(
        (value) => value !== null && value !== undefined
      ).length;
    },
    doneTitle() {
      return this.submissionExpired ? "Délai écoulé" : "Test terminé";
    },
    doneMessage() {
      if (this.submissionExpired) {
        return "Le délai imparti est écoulé. Votre tentative a été clôturée et vos réponses ont été enregistrées.";
      }
      return "Vos réponses ont bien été enregistrées. Merci d'avoir participé au test de sélection.";
    },
    doneAlertClass() {
      return this.submissionExpired ? "alert-warning" : "alert-success";
    },
  },
  mounted() {
    const token = localStorage.getItem("token");
    this.silentHttp = axios.create({
      baseURL: process.env.VUE_APP_API_ENDPOINT + "/api/candidate",
      headers: token ? { Authorization: "Bearer " + token } : {},
    });
    this.fetchState();
  },
  beforeUnmount() {
    this.cleanup();
  },
  methods: {
    fetchState() {
      this.$http
        .get("quiz")
        .then((response) => {
          const data = response.data;
          this.quizTitle = data.quizTitle;
          this.questionsCount = data.questionsCount;
          this.durationSeconds = data.durationSeconds;

          if (data.status === "disabled") {
            this.phase = "disabled";
          } else if (data.status === "not_submitted") {
            this.phase = "not_submitted";
          } else if (data.status === "submitted") {
            this.markAsDone(data.expired);
          } else if (data.status === "in_progress") {
            this.questions = data.questions || [];
            this.setRemainingFromServer(data.remainingSeconds);
            this.initAnswers();
            this.enterQuizMode();
          } else {
            this.phase = "intro";
          }
        })
        .catch(() => {
          this.phase = "intro";
          this.ToastError.fire({
            title: "Impossible de charger le test. Veuillez recharger la page.",
          });
        });
    },
    startQuiz() {
      this.starting = true;
      this.$http
        .post("quiz/start")
        .then((response) => {
          const data = response.data;
          this.quizTitle = data.quizTitle || this.quizTitle;
          this.durationSeconds = data.durationSeconds || this.durationSeconds;
          this.questions = data.questions || [];
          this.setRemainingFromServer(data.remainingSeconds);
          this.initAnswers();
          this.enterQuizMode();
        })
        .catch((error) => {
          if (error.response && error.response.status === 403) {
            this.loadDoneState();
            this.AlertWarning.fire({
              title: "Test déjà passé ou indisponible.",
            });
          } else {
            this.ToastError.fire({
              title: "Impossible de démarrer le test. Réessayez.",
            });
          }
        })
        .finally(() => {
          this.starting = false;
        });
    },
    initAnswers() {
      const answers = {};
      this.questions.forEach((question) => {
        answers[question.id] = null;
      });
      this.answers = answers;
    },
    markAsDone(expired) {
      this.submissionExpired = !!expired;
      this.finished = true;
      this.phase = "done";
      this.cleanup();
    },
    loadDoneState() {
      if (!this.silentHttp) {
        this.markAsDone(false);
        return;
      }

      this.silentHttp
        .get("quiz")
        .then((response) => {
          const data = response.data;
          this.markAsDone(data.status === "submitted" && data.expired);
        })
        .catch(() => {
          this.markAsDone(false);
        });
    },
    setRemainingFromServer(remainingSeconds) {
      const seconds =
        typeof remainingSeconds === "number" && !Number.isNaN(remainingSeconds)
          ? remainingSeconds
          : 0;
      this.remainingSeconds = Math.max(0, seconds);
      this.deadlineAt = Date.now() + this.remainingSeconds * 1000;
    },
    tickRemaining() {
      if (this.deadlineAt === null) {
        return;
      }

      this.remainingSeconds = Math.max(0, Math.ceil((this.deadlineAt - Date.now()) / 1000));

      if (this.remainingSeconds <= 0) {
        this.remainingSeconds = 0;
        this.autoSubmit("Le temps est écoulé. Vos réponses ont été soumises automatiquement.");
      }
    },
    syncRemainingFromServer() {
      if (this.phase !== "in_progress" || this.finished || this.submitting || !this.silentHttp) {
        return;
      }

      this.silentHttp
        .get("quiz")
        .then((response) => {
          const data = response.data;

          if (data.status === "submitted") {
            this.markAsDone(data.expired);
            return;
          }

          if (data.status === "in_progress" && typeof data.remainingSeconds === "number") {
            this.setRemainingFromServer(data.remainingSeconds);
          }
        })
        .catch(() => {});
    },
    enterQuizMode() {
      this.phase = "in_progress";
      this.startTimer();
      this.bindAntiCheat();
      this.requestFullscreen();
    },
    startTimer() {
      this.stopTimer();
      this.tickRemaining();
      this.timerId = setInterval(() => {
        this.tickRemaining();
      }, 1000);
      this.startSyncTimer();
    },
    startSyncTimer() {
      this.stopSyncTimer();
      this.syncTimerId = setInterval(() => {
        this.syncRemainingFromServer();
      }, this.timerSyncIntervalMs);
    },
    stopTimer() {
      if (this.timerId) {
        clearInterval(this.timerId);
        this.timerId = null;
      }
    },
    stopSyncTimer() {
      if (this.syncTimerId) {
        clearInterval(this.syncTimerId);
        this.syncTimerId = null;
      }
    },
    buildPayload() {
      const answers = this.questions.map((question) => ({
        questionId: question.id,
        selectedIndex:
          this.answers[question.id] === undefined ? null : this.answers[question.id],
      }));
      return { answers };
    },
    // SweetAlert must render inside the fullscreen element to stay visible.
    quizAlertTarget() {
      return this.$refs.quizRoot || document.body;
    },
    confirmSubmit() {
      const unanswered = this.questions.length - this.answeredCount;
      const text =
        unanswered > 0
          ? "Il vous reste " + unanswered + " question(s) sans réponse. Soumettre quand même ?"
          : "Confirmez-vous la soumission de vos réponses ?";

      Swal.fire({
        target: this.quizAlertTarget(),
        title: "Terminer le test",
        text,
        icon: "question",
        showCancelButton: true,
        confirmButtonText: "Soumettre",
        cancelButtonText: "Annuler",
        confirmButtonColor: "#0066b6",
      }).then((result) => {
        if (result.isConfirmed) {
          this.submitQuiz();
        }
      });
    },
    autoSubmit(message) {
      if (this.finished) {
        return;
      }
      this.submitQuiz(() => {
        this.AlertWarning.fire({ target: this.quizAlertTarget(), title: message });
      });
    },
    submitQuiz(onDone) {
      if (this.finished || this.submitting) {
        return;
      }
      this.submitting = true;
      this.stopTimer();

      this.$http
        .post("quiz/submit", this.buildPayload())
        .then((response) => {
          this.markAsDone(response.data && response.data.expired);
          if (typeof onDone === "function") {
            onDone();
          }
        })
        .catch((error) => {
          if (error.response && error.response.status === 403) {
            this.loadDoneState();
          } else {
            this.ToastError.fire({
              target: this.quizAlertTarget(),
              title: "Échec de la soumission. Vérifiez votre connexion et réessayez.",
            });
            this.startTimer();
          }
        })
        .finally(() => {
          this.submitting = false;
        });
    },
    // ---- Anti-cheat ----
    bindAntiCheat() {
      if (this.antiCheatBound) {
        return;
      }
      document.addEventListener("visibilitychange", this.onVisibilityChange);
      window.addEventListener("blur", this.onWindowBlur);
      document.addEventListener("contextmenu", this.onContextMenu);
      document.addEventListener("copy", this.onCopyPaste);
      document.addEventListener("cut", this.onCopyPaste);
      document.addEventListener("paste", this.onCopyPaste);
      document.addEventListener("fullscreenchange", this.onFullscreenChange);
      this.antiCheatBound = true;
    },
    unbindAntiCheat() {
      document.removeEventListener("visibilitychange", this.onVisibilityChange);
      window.removeEventListener("blur", this.onWindowBlur);
      document.removeEventListener("contextmenu", this.onContextMenu);
      document.removeEventListener("copy", this.onCopyPaste);
      document.removeEventListener("cut", this.onCopyPaste);
      document.removeEventListener("paste", this.onCopyPaste);
      document.removeEventListener("fullscreenchange", this.onFullscreenChange);
      this.antiCheatBound = false;
    },
    onVisibilityChange() {
      if (document.hidden) {
        this.recordViolation("tab_hidden");
      } else if (this.phase === "in_progress") {
        this.syncRemainingFromServer();
      }
    },
    onWindowBlur() {
      if (!document.hidden) {
        this.recordViolation("window_blur");
      }
    },
    onContextMenu(event) {
      event.preventDefault();
    },
    onCopyPaste(event) {
      event.preventDefault();
      this.recordViolation("clipboard");
    },
    onFullscreenChange() {
      if (!document.fullscreenElement && this.phase === "in_progress") {
        this.recordViolation("fullscreen_exit");
      }
    },
    requestFullscreen() {
      const element = this.$refs.quizRoot;
      if (element && element.requestFullscreen) {
        element.requestFullscreen().catch(() => {});
      }
    },
    recordViolation(type) {
      if (this.phase !== "in_progress" || this.finished) {
        return;
      }
      const now = Date.now();
      // Throttle to avoid double counting (e.g. blur + visibilitychange).
      if (now - this.lastViolationAt < 800) {
        return;
      }
      this.lastViolationAt = now;
      this.cheatViolations += 1;

      if (this.silentHttp) {
        this.silentHttp
          .post("quiz/event", { type })
          .then((response) => {
            if (response.data && response.data.cheatViolations) {
              this.cheatViolations = response.data.cheatViolations;
              this.evaluateViolations();
            }
          })
          .catch(() => {});
      }

      this.ToastWarning.fire({
        target: this.quizAlertTarget(),
        title: "Veuillez rester sur la page du test en plein écran.",
      });

      this.evaluateViolations();
    },
    evaluateViolations() {
      // Reserved for future auto-submit logic based on cheatViolations.
    },
    cleanup() {
      this.stopTimer();
      this.stopSyncTimer();
      this.deadlineAt = null;
      this.unbindAntiCheat();
      if (document.fullscreenElement && document.exitFullscreen) {
        document.exitFullscreen().catch(() => {});
      }
    },
  },
};
</script>

<style scoped>
.quiz-wrapper {
  user-select: none;
  position: relative;
}

.quiz-wrapper:fullscreen {
  overflow-y: auto;
  width: 100%;
  height: 100%;
  max-width: none;
}

.quiz-wrapper:-webkit-full-screen {
  overflow-y: auto;
  width: 100%;
  height: 100%;
  max-width: none;
}

.quiz-topbar {
  position: sticky;
  top: 0;
  z-index: 10;
  background: #fff;
  padding: 0.75rem 1rem;
  border-radius: 0.5rem;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.1);
}

.quiz-timer {
  font-weight: bold;
  font-size: 1.1rem;
}

.quiz-question .card-title {
  font-size: 1.05rem;
  line-height: 1.5;
}

.quiz-option {
  padding: 0.4rem 0.4rem 0.4rem 2rem;
  border-radius: 0.35rem;
}

.quiz-option:hover {
  background-color: #f1f6fb;
}

.section-title {
  color: #0066b6;
}
</style>
